import * as dragula from 'dragula';

import { logError } from '../error_logging';
import { SortableMove } from './reorder';

export interface SortableOptions {
  // CSS class of the drag handle inside each row.
  handleClass: string;
  // Attribute holding each row's stable key. Rows without it take no part in a move.
  keyAttribute: string;
  // Whether dragging is currently allowed.
  canMove: () => boolean;
  // CSS class toggled on the row while it is being dragged.
  draggingClass?: string;
  // Called after a drop with the move the user made. The DOM has already been
  // restored to its pre-drag state, so the callback should update the backing
  // Knockout observable(s) and let Knockout re-render.
  onMove: (move: SortableMove) => void;
}

/**
 * Wires dragula to a Knockout-rendered list so rows can be reordered by
 * dragging a handle.
 *
 * The non-obvious part it encapsulates: on drop, the move is captured and then the
 * dragged node is moved back to its pre-drag position BEFORE the observable is
 * updated. Knockout's foreach diff is computed against the array it last rendered; if
 * drake leaves the DOM rearranged, the diff's moves land on the wrong nodes and the
 * visible order doesn't update. (drake.cancel(true) does not work — it breaks
 * Knockout's re-render.)
 *
 * A list can render across several containers, so one container's rows are only part
 * of it.
 */
export class Sortable {
  private drake: dragula.Drake | null = null;
  private originalNextSibling: Node | null = null;

  constructor(
    private readonly container: HTMLElement,
    private readonly options: SortableOptions
  ) {
    this.init();
  }

  // (Re)create the dragula instance. Call again after the container's rows are
  // re-rendered (e.g. pagination or add/remove).
  init = () => {
    this.destroy();

    this.drake = dragula([this.container], {
      revertOnSpill: true,
      moves: (_element, _container, handle) => {
        if (!this.options.canMove()) {
          return false;
        }
        return handle.classList.contains(this.options.handleClass);
      },
    });

    this.drake.on('drag', (element: HTMLElement) => {
      this.originalNextSibling = element.nextSibling;
      if (this.options.draggingClass) {
        element.classList.add(this.options.draggingClass);
      }
    });
    this.drake.on('dragend', (element: HTMLElement) => {
      if (this.options.draggingClass) {
        element.classList.remove(this.options.draggingClass);
      }
    });
    this.drake.on(
      'drop',
      (element: HTMLElement, _target: HTMLElement, source: HTMLElement, sibling: HTMLElement) => {
        const move = this.describeMove(element, sibling);
        if (source) {
          source.insertBefore(element, this.originalNextSibling);
        }
        if (move) {
          this.options.onMove(move);
        }
      }
    );
  };

  destroy = () => {
    if (this.drake) {
      this.drake.destroy();
      this.drake = null;
    }
  };

  private keyOf = (element: Element | null): string | null => {
    return element ? element.getAttribute(this.options.keyAttribute) : null;
  };

  // Read while the DOM still holds the drop result, so `element` sits in its new place.
  private describeMove = (element: Element, sibling: Element | null): SortableMove | null => {
    const movedKey = this.keyOf(element);
    if (!movedKey) {
      // Only reachable if a template stops rendering the key, which turns every drag
      // in that container into a silent no-op.
      logError(`Sortable: dragged row has no ${this.options.keyAttribute}`, null);
      return null;
    }

    // dragula reports the node following the drop point, which can be a section header
    // rather than a row, so walk on to the next keyed one.
    const anchorAfter = this.findKey(sibling, element, 'nextElementSibling');
    if (anchorAfter) {
      return { movedKey, position: 'before', anchorKey: anchorAfter };
    }

    const anchorBefore = this.findKey(element.previousElementSibling, element, 'previousElementSibling');
    if (anchorBefore) {
      return { movedKey, position: 'after', anchorKey: anchorBefore };
    }

    return { movedKey, position: 'only' };
  };

  private findKey = (
    from: Element | null,
    moved: Element,
    step: 'nextElementSibling' | 'previousElementSibling'
  ): string | null => {
    for (let node = from; node; node = node[step]) {
      if (node === moved) {
        continue;
      }
      const key = this.keyOf(node);
      if (key) {
        return key;
      }
    }
    return null;
  };
}
